/**
 * The slim legal line (legal-pages ticket 05):
 *
 *   Terms · Privacy · © 2026 {LEGAL_ENTITY}
 *
 * It draws nothing until the legal pages are published (`legal.published`), so
 * an unpublished build is the page as it was.
 *
 * The links are built from the documents that exist (`legal.documents`), in
 * hub order, so a page added to `frontend/legal/` appears here with no change.
 * The footer shows a short name, not the document's title, and only for the
 * documents listed below; the rest (the Auto-Apply terms) are one click
 * further, on the hub every legal page links to.
 *
 * `newTab` is for a screen holding something a navigation would lose: a
 * half-typed email, a chosen file.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { legal } from '../lib/legal';
import './LegalFooter.css';

/** Footer names by document slug. The later ones have no page yet. */
const FOOTER_LABELS: Record<string, string> = {
  terms: 'Terms',
  privacy: 'Privacy',
  'privacy-choices': 'Your Privacy Choices',
  cookies: 'Cookies',
  accessibility: 'Accessibility',
};

/** The year the landing footer has always carried. */
const COPYRIGHT_YEAR = 2026;

/** Who the copyright line names: the registered entity once published. */
export const legalCopyrightName = (): string => (legal.published ? legal.entity : 'ClimbJobs');

const footerDocuments = () => (legal.published ? legal.documents.filter((d) => d.slug in FOOTER_LABELS) : []);

/** The links alone, each followed by its separator, for a footer that already
 *  has a copyright line of its own (the landing page). Nothing when unpublished. */
export function LegalLinks({ newTab = false }: { newTab?: boolean }) {
  const docs = footerDocuments();
  if (!docs.length) return null;
  return (
    <span className="cj-legal-links" data-legal-links="">
      {docs.map((d) => (
        <React.Fragment key={d.slug}>
          {newTab
            ? <a className="cj-legal-link" href={d.path} target="_blank" rel="noopener">{FOOTER_LABELS[d.slug]}</a>
            : <Link className="cj-legal-link" to={d.path}>{FOOTER_LABELS[d.slug]}</Link>}
          <span className="cj-legal-sep" aria-hidden="true">·</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/** The whole line. `app` is the signed-in shell's variant: its tokens, left
 *  aligned, and inside the bottom padding the page already has. `tuck` does
 *  the same on the sign-in screen, which is exactly one viewport tall. */
export function LegalFooter({ newTab = false, app = false, tuck = false }: { newTab?: boolean; app?: boolean; tuck?: boolean }) {
  if (!legal.published) return null;
  return (
    <footer className={'cj-legal-foot' + (app ? ' cj-legal-foot--app' : '') + (tuck ? ' cj-legal-foot--tuck' : '')} data-legal-footer="">
      <LegalLinks newTab={newTab} />
      © {COPYRIGHT_YEAR} {legalCopyrightName()}
    </footer>
  );
}
